<template>
  <div class="collection-picker" data-testid="collection-picker">
    <div class="picker-header">
      <Button type="link" variant="gray" icon="general.backArrow" text="Back" @click="handleCancel" data-testid="collection-picker-back" />
      <span class="divider">|</span>
      <h6 class="title">Select a collection</h6>
    </div>

    <div class="picker-content">
      <!-- Loading state with skeletons -->
      <ul v-if="isLoading && filteredCollections.length === 0" class="collections-list" data-testid="collection-picker-loading">
        <li v-for="i in 5" :key="`skeleton-${i}`" class="collection-item">
          <div class="collection-skeleton animate-pulse">
            <div class="skeleton-info">
              <div class="skeleton skeleton-name"></div>
              <div class="skeleton skeleton-badge"></div>
            </div>
          </div>
        </li>
      </ul>

      <!-- Empty state -->
      <div v-else-if="!isLoading && filteredCollections.length === 0" class="empty-state" data-testid="collection-picker-empty">
        <Icon name="general.folder" size="large" class="empty-icon" />
        <p class="empty-message">You don't have any collections yet</p>
      </div>

      <!-- Collections list -->
      <ul v-else class="collections-list" data-testid="collection-picker-list">
        <li v-for="collection in filteredCollections" :key="collection.id" class="collection-item" :data-testid="`collection-item-${collection.id}`">
          <button class="collection-button" :class="{ selected: isSelected(collection) }" @click="handleSelect(collection)">
            <div class="collection-info">
              <Icon name="general.folder" size="small" class="collection-icon" />
              <span class="collection-name">{{ collection.name }}</span>
              <span class="collection-badge">{{ collection.items?.length || 0 }}</span>
            </div>
            <Icon v-if="isSelected(collection)" name="general.check" size="small" class="check-icon" />
          </button>
        </li>

        <!-- Load more skeleton -->
        <li v-if="hasMore && isLoading" class="collection-item">
          <div class="collection-skeleton animate-pulse">
            <div class="skeleton-info">
              <div class="skeleton skeleton-name"></div>
              <div class="skeleton skeleton-badge"></div>
            </div>
          </div>
        </li>

        <!-- Load more trigger (invisible) -->
        <li v-if="hasMore" ref="loadMoreTrigger" class="load-more-trigger" data-testid="load-more-trigger"></li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button, Icon } from '@/storybook';
import { useCollectionPicker } from '@/composables/useCollectionPicker';
import type { CollectionItemInterface } from '@/store/useCollectionsStore';

interface Props {
  selectedId?: string | null;
  excludeSecure?: boolean;
  excludeOnboarding?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  selectedId: null,
  excludeSecure: true,
  excludeOnboarding: true,
});

interface Emits {
  select: [collection: CollectionItemInterface];
  cancel: [];
}

const emit = defineEmits<Emits>();

const { isLoading, filteredCollections, hasMore, loadMoreTrigger, isSelected, handleSelect, handleCancel } = useCollectionPicker({
  selectedId: props.selectedId,
  excludeSecure: props.excludeSecure,
  excludeOnboarding: props.excludeOnboarding,
  onSelect: collection => emit('select', collection),
  onCancel: () => emit('cancel'),
});
</script>

<style scoped>
.collection-picker {
  @apply flex flex-col h-full w-full max-w-full;
  min-width: 0; /* Allow shrinking below content size */
}

.picker-header {
  @apply flex items-center gap-2 mb-4 text-zinc-700 dark:text-zinc-300;
}

.divider {
  @apply text-zinc-400 dark:text-zinc-500;
}

.title {
  @apply m-0 text-base font-semibold text-zinc-900 dark:text-zinc-100;
}

.picker-content {
  @apply flex-1 overflow-hidden;
}

.empty-state {
  @apply flex flex-col items-center justify-center h-52 text-center px-4;
}

.empty-icon {
  @apply text-zinc-300 dark:text-zinc-600 mb-3;
}

.empty-message {
  @apply text-zinc-500 dark:text-zinc-400;
}

.collections-list {
  @apply flex flex-col gap-2 max-h-96 overflow-y-auto px-1;
}

.collection-item {
  @apply list-none;
}

.collection-button {
  @apply w-full flex items-center justify-between gap-2 px-3 py-2.5
         text-left bg-white dark:bg-zinc-800
         border border-zinc-200 dark:border-zinc-700
         rounded-lg transition-all
         hover:bg-zinc-50 dark:hover:bg-zinc-700
         hover:border-zinc-300 dark:hover:border-zinc-600
         cursor-pointer;
}

.collection-button.selected {
  @apply bg-blue-50 dark:bg-blue-900/20
         border-blue-200 dark:border-blue-800
         hover:bg-blue-100 dark:hover:bg-blue-900/30;
}

.collection-info {
  @apply flex items-center gap-2 min-w-0 flex-1;
}

.collection-icon {
  @apply text-zinc-400 dark:text-zinc-500 flex-shrink-0;
}

.collection-name {
  @apply text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate min-w-0;
}

.collection-badge {
  @apply px-1.5 py-0.5 text-xs font-medium
         bg-zinc-100 dark:bg-zinc-700
         text-zinc-600 dark:text-zinc-300
         rounded;
}

.check-icon {
  @apply text-blue-500 dark:text-blue-400;
}

.load-more-trigger {
  @apply h-1 invisible;
}

/* Skeleton styles - following existing pattern */
.collection-skeleton {
  @apply w-full flex items-center justify-between gap-2 px-3 py-2.5
         bg-zinc-50 dark:bg-zinc-800
         border border-zinc-200 dark:border-zinc-700
         rounded-lg;
}

.skeleton-info {
  @apply flex items-center gap-2;
}

.skeleton {
  @apply bg-zinc-200 dark:bg-zinc-700 rounded;
}

.skeleton-name {
  @apply h-4 w-32;
}

.skeleton-badge {
  @apply h-5 w-8;
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
